import { zodResolver } from '@hookform/resolvers/zod';
import { Calculator } from 'lucide-react';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  CurrencyInput,
  FormField,
  Input,
  PercentageInput,
} from '@payment-plan/ui';
import { EYEBROW_CLASS, SECTION_CARD_CLASS, TOUCH_INPUT_CLASS } from './field-styles';
import type { FlowDraft, PaymentPlanPreview } from '../../../types/separations';
import { mensajeDeError } from '../errors';
import { formatCOP, formatFecha, hoyISO, toPesos } from '../money';
import {
  MAX_CUOTAS,
  conditionsSchema,
  toCalculateInput,
  toConditionsInput,
  type ConditionsFormValues,
} from '../separation.schema';
import { useCalculatePlan, useSaveConditions } from '../use-separation-flow';

const TIPO_ADICIONAL: Record<string, string> = { PARQUEADERO: 'Parqueadero', DEPOSITO: 'Depósito' };

interface ConditionsStepProps {
  draft: FlowDraft;
  initialValues?: ConditionsFormValues;
  initialPlan?: PaymentPlanPreview | null;
  onBack?: () => void;
  onContinue: (plan: PaymentPlanPreview, values: ConditionsFormValues) => void;
}

/**
 * fix-50 (B-02, ADR-017): el asesor negocia la unidad PRINCIPAL; se prellena con su precio de lista.
 * Las adicionales van a precio de lista y el total lo compone el back.
 */
function defaults(draft: FlowDraft): ConditionsFormValues {
  const listaPrincipal = toPesos(draft.unidad.precioLista);
  return {
    precioNegociado: listaPrincipal > 0n ? listaPrincipal.toString() : '',
    valorSeparacion: '',
    modoCuotaInicial: 'PORCENTAJE',
    porcentajeCuotaInicial: 0,
    valorCuotaInicial: '',
    numeroCuotas: '',
    diaPago: '',
    fechaSeparacion: hoyISO(),
    fechaEscrituracion: '',
  };
}

const toBigInt = (digits: string): bigint => (/^\d+$/.test(digits) ? BigInt(digits) : 0n);
const toDigits = (value: bigint): string => (value > 0n ? value.toString() : '');

export function ConditionsStep({
  draft,
  initialValues,
  initialPlan = null,
  onBack,
  onContinue,
}: ConditionsStepProps): React.JSX.Element {
  const form = useForm<ConditionsFormValues>({
    resolver: zodResolver(conditionsSchema),
    defaultValues: initialValues ?? defaults(draft),
  });
  const { control, register, handleSubmit, watch, formState } = form;
  const errors = formState.errors;
  const calcular = useCalculatePlan();
  const guardarCondiciones = useSaveConditions();
  const [continuarError, setContinuarError] = React.useState<string | null>(null);
  const [plan, setPlan] = React.useState<PaymentPlanPreview | null>(initialPlan);
  const [desactualizado, setDesactualizado] = React.useState(false);
  const [confirmado, setConfirmado] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const modo = watch('modoCuotaInicial');

  React.useEffect(() => {
    const sub = watch(() => setDesactualizado(true));
    return () => sub.unsubscribe();
  }, [watch]);

  const onSubmit = (values: ConditionsFormValues): void => {
    setServerError(null);
    setContinuarError(null);
    calcular.mutate(toCalculateInput(values, draft), {
      onSuccess: (resultado) => {
        setPlan(resultado);
        setDesactualizado(false);
        setConfirmado(false);
      },
      onError: (e) => setServerError(mensajeDeError(e, 'calcular el plan de pagos')),
    });
  };

  const bloqueantes = plan?.validations.filter((v) => v.severity === 'error') ?? [];
  const advertencias = plan?.validations.filter((v) => v.severity === 'warning') ?? [];
  const puedeContinuar =
    !!plan &&
    !desactualizado &&
    bloqueantes.length === 0 &&
    plan.installments.length > 0 &&
    (advertencias.length === 0 || confirmado);

  return (
    <div className="space-y-6">
      <form noValidate onSubmit={handleSubmit(onSubmit)} aria-labelledby="paso-condiciones" className={SECTION_CARD_CLASS}>
        <div>
          <p className={EYEBROW_CLASS}>Paso 3</p>
          <h2 id="paso-condiciones" className="text-xl font-semibold tracking-tight text-foreground">
            Condiciones comerciales
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            El sistema calcula el plan con estas condiciones. Revisa la tabla antes de continuar.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <Controller
            control={control}
            name="precioNegociado"
            render={({ field }) => (
              <FormField
                label="Precio negociado de la unidad"
                htmlFor="cond-precio"
                required
                description={`Precio de lista de la unidad: ${formatCOP(draft.unidad.precioLista)}`}
                error={errors.precioNegociado?.message}
              >
                <CurrencyInput
                  id="cond-precio"
                  className={TOUCH_INPUT_CLASS}
                  value={toBigInt(field.value)}
                  onChange={(v) => field.onChange(toDigits(v))}
                  onBlur={field.onBlur}
                  error={!!errors.precioNegociado}
                />
              </FormField>
            )}
          />

          {draft.adicionales.length > 0 && (
            <section
              aria-label="Unidades adicionales"
              className="space-y-1.5 rounded-lg border border-border/70 bg-surface-fade p-3 md:col-span-2"
            >
              <p className="text-sm font-medium text-foreground">
                Unidades adicionales <span className="font-normal text-muted-foreground">(a precio de lista)</span>
              </p>
              <ul className="flex flex-col gap-1 text-sm lg:flex-row lg:flex-wrap lg:gap-x-6">
                {draft.adicionales.map((u) => (
                  <li key={u.id} className="flex justify-between gap-3 lg:justify-start">
                    <span>
                      {u.numero} · {TIPO_ADICIONAL[u.tipo] ?? u.tipo}
                    </span>
                    <span className="whitespace-nowrap tabular-nums text-muted-foreground">{formatCOP(u.precioLista)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                El sistema suma estas unidades al precio negociado para obtener el precio total.
              </p>
            </section>
          )}

          <Controller
            control={control}
            name="valorSeparacion"
            render={({ field }) => (
              <FormField label="Valor de separación" htmlFor="cond-separacion" required error={errors.valorSeparacion?.message}>
                <CurrencyInput
                  id="cond-separacion"
                  className={TOUCH_INPUT_CLASS}
                  value={toBigInt(field.value)}
                  onChange={(v) => field.onChange(toDigits(v))}
                  onBlur={field.onBlur}
                  error={!!errors.valorSeparacion}
                />
              </FormField>
            )}
          />

          <fieldset className="space-y-2 md:col-span-2">
            <legend className="text-sm font-medium text-foreground">Cuota inicial</legend>
            <div role="radiogroup" aria-label="Forma de la cuota inicial" className="flex flex-wrap gap-2">
              {(
                [
                  ['PORCENTAJE', 'Porcentaje'],
                  ['VALOR', 'Valor fijo'],
                ] as const
              ).map(([valor, label]) => (
                <label
                  key={valor}
                  className="flex min-h-11 cursor-pointer items-center gap-2 rounded-md border border-input px-3 text-sm has-[:checked]:border-brand-gold has-[:checked]:bg-gold-sheen has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring"
                >
                  <input type="radio" value={valor} className="h-4 w-4 accent-primary" {...register('modoCuotaInicial')} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          {modo === 'PORCENTAJE' ? (
            <Controller
              control={control}
              name="porcentajeCuotaInicial"
              render={({ field }) => (
                <FormField
                  label="Cuota inicial (%)"
                  htmlFor="cond-ci-porcentaje"
                  required
                  error={errors.porcentajeCuotaInicial?.message}
                >
                  <PercentageInput
                    id="cond-ci-porcentaje"
                    className={TOUCH_INPUT_CLASS}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={!!errors.porcentajeCuotaInicial}
                  />
                </FormField>
              )}
            />
          ) : (
            <Controller
              control={control}
              name="valorCuotaInicial"
              render={({ field }) => (
                <FormField
                  label="Cuota inicial (valor)"
                  htmlFor="cond-ci-valor"
                  required
                  error={errors.valorCuotaInicial?.message}
                >
                  <CurrencyInput
                    id="cond-ci-valor"
                    className={TOUCH_INPUT_CLASS}
                    value={toBigInt(field.value)}
                    onChange={(v) => field.onChange(toDigits(v))}
                    onBlur={field.onBlur}
                    error={!!errors.valorCuotaInicial}
                  />
                </FormField>
              )}
            />
          )}

          <FormField
            label="Número de cuotas"
            htmlFor="cond-cuotas"
            required
            description={`Entre 1 y ${MAX_CUOTAS}`}
            error={errors.numeroCuotas?.message}
          >
            <Input
              id="cond-cuotas"
              inputMode="numeric"
              className={TOUCH_INPUT_CLASS}
              error={!!errors.numeroCuotas}
              {...register('numeroCuotas')}
            />
          </FormField>

          <FormField
            label="Día de pago"
            htmlFor="cond-dia"
            required
            description="Día del mes en que vence cada cuota"
            error={errors.diaPago?.message}
          >
            <Input id="cond-dia" inputMode="numeric" className={TOUCH_INPUT_CLASS} error={!!errors.diaPago} {...register('diaPago')} />
          </FormField>

          <FormField label="Fecha de separación" htmlFor="cond-fecha-sep" required error={errors.fechaSeparacion?.message}>
            <Input
              id="cond-fecha-sep"
              type="date"
              max={hoyISO()}
              className={TOUCH_INPUT_CLASS}
              error={!!errors.fechaSeparacion}
              {...register('fechaSeparacion')}
            />
          </FormField>

          <FormField
            label="Fecha estimada de escrituración"
            htmlFor="cond-fecha-esc"
            required
            error={errors.fechaEscrituracion?.message}
          >
            <Input
              id="cond-fecha-esc"
              type="date"
              className={TOUCH_INPUT_CLASS}
              error={!!errors.fechaEscrituracion}
              {...register('fechaEscrituracion')}
            />
          </FormField>
        </div>

        {serverError && (
          <p role="alert" className="text-sm text-destructive">
            {serverError}
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 lg:flex-row lg:justify-between">
          {onBack ? (
            <Button type="button" variant="outline" size="lg" onClick={onBack}>
              Volver a separantes
            </Button>
          ) : (
            <span />
          )}
          <Button type="submit" size="lg" loading={calcular.isPending}>
            <Calculator className="h-4 w-4" aria-hidden="true" />
            Calcular plan
          </Button>
        </div>
      </form>

      {plan && (
        <PlanPreview
          plan={plan}
          bloqueantes={bloqueantes}
          advertencias={advertencias}
          desactualizado={desactualizado}
          confirmado={confirmado}
          onConfirmar={setConfirmado}
          puedeContinuar={puedeContinuar && !guardarCondiciones.isPending}
          continuando={guardarCondiciones.isPending}
          continuarError={continuarError}
          onContinuar={() => {
            // fix-50: persistir las condiciones antes de las cuotas (calcular → PUT /condiciones → PUT /cuotas).
            const values = form.getValues();
            setContinuarError(null);
            guardarCondiciones.mutate(
              { id: draft.id, condiciones: toConditionsInput(values) },
              {
                onSuccess: () => onContinue(plan, values),
                onError: (e) => setContinuarError(mensajeDeError(e, 'guardar las condiciones comerciales')),
              },
            );
          }}
        />
      )}
    </div>
  );
}

interface PlanPreviewProps {
  plan: PaymentPlanPreview;
  bloqueantes: PaymentPlanPreview['validations'];
  advertencias: PaymentPlanPreview['validations'];
  desactualizado: boolean;
  confirmado: boolean;
  onConfirmar: (v: boolean) => void;
  puedeContinuar: boolean;
  continuando: boolean;
  continuarError: string | null;
  onContinuar: () => void;
}

function PlanPreview({
  plan,
  bloqueantes,
  advertencias,
  desactualizado,
  confirmado,
  onConfirmar,
  puedeContinuar,
  continuando,
  continuarError,
  onContinuar,
}: PlanPreviewProps): React.JSX.Element {
  // fix-50 (ADR-017): la composición del precio la devuelve el back; aquí solo se formatea.
  const composicion: Array<[string, string]> = plan.priceComposition
    ? [
        ['Precio de la unidad', formatCOP(plan.priceComposition.negotiatedMainPrice)],
        ['Unidades adicionales', formatCOP(plan.priceComposition.additionalUnitsListPrice)],
      ]
    : [];
  const resumen: Array<[string, string]> = [
    ...composicion,
    ['Precio total', formatCOP(plan.totalPrice)],
    ['Valor de separación', formatCOP(plan.separationValue)],
    ['Cuota inicial', formatCOP(plan.initialPayment)],
    ['Saldo a diferir', formatCOP(plan.balanceToDefer)],
    ['Saldo a crédito', formatCOP(plan.creditBalance)],
  ];

  return (
    <section aria-labelledby="preview-titulo" className={SECTION_CARD_CLASS}>
      <div>
        <p className={EYEBROW_CLASS}>Preview del sistema</p>
        <h3 id="preview-titulo" className="text-lg font-semibold tracking-tight text-foreground">
          Plan de pagos calculado
        </h3>
      </div>

      {desactualizado && (
        <Alert variant="info">
          <AlertDescription>Cambiaste las condiciones: vuelve a calcular el plan antes de continuar.</AlertDescription>
        </Alert>
      )}

      {bloqueantes.length > 0 && (
        <Alert variant="destructive" role="alert" aria-labelledby="preview-errores">
          <AlertTitle id="preview-errores">El plan no se puede generar</AlertTitle>
          <ul className="list-disc space-y-1 pl-4 text-sm">
            {bloqueantes.map((v) => (
              <li key={v.code}>{v.message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {advertencias.map((v) => (
        <Alert key={v.code} variant="warning">
          <AlertDescription className="text-foreground">{v.message}</AlertDescription>
          <div className="mt-2 flex items-center gap-3">
            <Checkbox
              id={`confirmar-${v.code}`}
              className="h-5 w-5"
              checked={confirmado}
              onCheckedChange={(c) => onConfirmar(c === true)}
            />
            <label htmlFor={`confirmar-${v.code}`} className="cursor-pointer text-sm font-medium text-foreground">
              {v.code === 'PRICE_BELOW_LIST' ? 'Confirmo el descuento y deseo continuar' : 'Confirmo y deseo continuar'}
            </label>
          </div>
        </Alert>
      ))}

      <section aria-label="Resumen del plan" className="rounded-lg border border-border/70 bg-surface-fade p-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4">
          {resumen.map(([dt, dd]) => (
            <div key={dt}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dt}</dt>
              <dd className="mt-0.5 whitespace-nowrap text-base font-semibold tabular-nums text-foreground">{dd}</dd>
            </div>
          ))}
        </dl>
        {plan.totals.totalPriceInWords && (
          <p className="mt-3 text-xs font-medium tracking-wide text-muted-foreground">{plan.totals.totalPriceInWords}</p>
        )}
      </section>

      {plan.installments.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border/70">
          <table aria-label="Cuotas calculadas" className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                {['N.º', 'Vencimiento', 'Tipo', 'Valor', 'Saldo'].map((h) => (
                  <th key={h} scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {plan.installments.map((c) => (
                <tr key={`${c.number}-${c.dueDate}`} className="border-t border-border/60">
                  <td className="px-3 py-2 tabular-nums">{c.number}</td>
                  <td className="whitespace-nowrap px-3 py-2 tabular-nums">{formatFecha(c.dueDate)}</td>
                  <td className="px-3 py-2">{c.type === 'EXTRAORDINARIA' ? 'Extraordinaria' : 'Ordinaria'}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatCOP(c.value)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">{formatCOP(c.balanceAfter)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-muted/30 font-semibold">
                <td className="px-3 py-2" colSpan={3}>
                  Total cuotas
                </td>
                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatCOP(plan.totals.totalInstallments)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {continuarError && (
        <p role="alert" className="text-sm text-destructive">
          {continuarError}
        </p>
      )}

      <div className="flex justify-end">
        <Button size="lg" className="w-full lg:w-auto" disabled={!puedeContinuar} loading={continuando} onClick={onContinuar}>
          Continuar a ajustar cuotas
        </Button>
      </div>
    </section>
  );
}
